import { useMemo, useState, useRef } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  FaBuilding,
  FaSpinner,
} from "react-icons/fa";
import SideModal from "../SideModal";
import RentDefaultersModal from "./RentDefaulterModal";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  FaBed,
  FaDoorOpen,
  FaUserCheck,
  FaUserPlus,
  FaRupeeSign,
  FaExclamationCircle,
  FaClipboardList,
  FaBullhorn,
  FaCheckCircle,
  FaSignOutAlt,
  FaCalendarCheck,
  FaCalendarAlt,
  FaChevronUp,
  FaChevronDown,
  FaFileDownload,
  FaUserSlash,
  FaWallet,
} from "react-icons/fa";
import {
  MdMeetingRoom,
  MdOutlinePendingActions,
  MdPayment,
} from "react-icons/md";

const getMonthLabel = () =>
  new Date().toLocaleDateString("en-IN", { month: "long" });

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const metricGroups = [
  {
    title: "Property Overview",
    metrics: [
      { key: "totalRooms", label: "Rooms", icon: <MdMeetingRoom size={14} />, color: "indigo" },
      { key: "totalBeds", label: "Beds", icon: <FaBed size={13} />, color: "blue" },
      { key: "occupiedBeds", label: "Occupied", icon: <FaCheckCircle size={13} />, color: "green" },
      { key: "vacantBeds", label: "Vacant", icon: <FaDoorOpen size={13} />, color: "amber" },
    ],
  },
  {
    title: "Financial Overview",
    metrics: [
      { key: "todayCollection", label: "Today's Collection", icon: <FaRupeeSign size={13} />, color: "green", type: "currency" },
      { key: "todayCreatedDue", label: "Today's Dues", icon: <FaExclamationCircle size={13} />, color: "red", type: "currency" },
      { key: "monthCollection", label: "Monthly Collection", icon: <MdPayment size={14} />, color: "green", type: "currency", monthLabel: true },
      { key: "monthPendingDue", label: "Monthly Dues", icon: <FaExclamationCircle size={13} />, color: "red", type: "currency", monthLabel: true },
      { key: "totalPendingDue", label: "Total Dues", icon: <FaClipboardList size={13} />, color: "red", type: "currency" },
      { key: "monthExpenses", label: "Monthly Expense", icon: <FaWallet size={13} />, color: "red", type: "currency", monthLabel: true },
    ],
  },
  {
    title: "Tenants",
    metrics: [
      { key: "activeTenants", label: "Active Tenants", icon: <FaUserCheck size={13} />, color: "green" },
      { key: "underEvictionTenants", label: "Moving Out", icon: <FaSignOutAlt size={13} />, color: "orange" },
      { key: "movingOutBedsWithBookings", label: "Moving Out – Booked Beds", icon: <FaCalendarCheck size={13} />, color: "amber" },
      { key: "newTenants", label: "New Tenants", icon: <FaUserPlus size={13} />, color: "green" },
      { key: "reservedTenants", label: "Current Bookings", icon: <FaCalendarAlt size={13} />, color: "yellow" },
      { key: "rentDefaulters", label: "Rent Defaulters", icon: <FaUserSlash size={13} />, color: "red" },
    ],
  },
  {
    title: "Operations",
    metrics: [
      { key: "activeLeads", label: "Active Leads", icon: <FaBullhorn size={13} />, color: "blue" },
      { key: "activeComplaints", label: "Active Complaints", icon: <FaExclamationCircle size={13} />, color: "red" },
      { key: "pendingRequests", label: "Pending Requests", icon: <MdOutlinePendingActions size={14} />, color: "amber" },
    ],
  },
];
const colorClasses = {
  slate: "bg-slate-100 text-slate-600",
  green: "bg-green-50 text-green-700",
  red: "bg-rose-50 text-rose-500",
  orange: "bg-orange-50 text-orange-700",
  purple: "bg-purple-50 text-purple-700",
  yellow: "bg-amber-50 text-amber-700",
  cyan: "bg-cyan-50 text-cyan-700",
};

const attentionMetrics = [
  "totalDues",
  "monthlyDues",
  "rentDefaulters",
  "activeComplaints",
];

const formatMetricValue = (property, metric) => {
  const value = property[metric.key] ?? 0;
  return metric.type === "currency"
    ? formatCurrency(value)
    : Number(value).toLocaleString("en-IN");
};

const getNavigationForMetric = (metricKey) => {
  const today = moment();

  // ---- Collections ----
  if (metricKey === "todayCollection") {
    return {
      route: "/finance/collections",
      startDate: today.format("YYYY-MM-DD"),
      endDate: today.format("YYYY-MM-DD"),
    };
  }
  if (metricKey === "monthCollection") {
    return {
      route: "/finance/collections",
      startDate: today.clone().startOf("month").format("YYYY-MM-DD"),
      endDate: today.format("YYYY-MM-DD"),
    };
  }

  // ---- Dues ----
  if (metricKey === "todayCreatedDue") {
    return {
      route: "/finance/dues",
      startDate: today.format("YYYY-MM-DD"),
      endDate: today.format("YYYY-MM-DD"),
    };
  }
  if (metricKey === "monthPendingDue") {
    return {
      route: "/finance/dues",
      startDate: today.clone().startOf("month").format("YYYY-MM-DD"),
      endDate: today.clone().endOf("month").format("YYYY-MM-DD"),
    };
  }
  if (metricKey === "totalPendingDue") {
    return {
      route: "/finance/dues",
      // startDate: "2020-01-01",
      // endDate: moment().format("YYYY-MM-DD"),
    };
  }

  // -- Expense
  if (metricKey === "monthExpenses") {
    return {
      route: "/finance/expenses",
    };
  }

  // -- Lead
  if (metricKey === "activeLeads") {
    return {
      route: "/leads",
    };
  }

  // -- Complaint
  if (metricKey === "activeComplaints") {
    return {
      route: "/complaints",
    };
  }

  // -- Complaint
  if (metricKey === "pendingRequests") {
    return {
      route: "/requests",
    };
  }

  // -- Tenant
  if (metricKey === "activeTenants" || metricKey === "newTenants") {
    return {
      route: "/tenants/active",
      fiter: ""
    };
  }
  if (metricKey === "underEvictionTenants") {
    return {
      route: "/tenants/active",
      filter: "M"
    };
  }
  if (metricKey === "reservedTenants") {
    return {
      route: "/tenants/active",
      filter: "R"
    };
  }

  return null;
};

function PropertyMetric({ property, metric, onMetricClick }) {
  const value = Number(property[metric.key] ?? 0);
  const needsAttention = attentionMetrics.includes(metric.key) && value > 0;

  const label = metric.monthLabel
    ? `${getMonthLabel()}'s ${metric.label.replace(/^Monthly /, "")}`
    : metric.label;

  const hasRoute =
    metric.key === "rentDefaulters" ||
    Boolean(getNavigationForMetric(metric.key));

  const isNavigable = hasRoute && value > 0;

  return (
    <button
      type="button"
      onClick={() => isNavigable && onMetricClick?.(property, metric)}
      disabled={!isNavigable}
      className={`group flex min-h-[43px] w-full items-center gap-3 border-b border-slate-100/80 px-3 text-left transition-colors last:border-b-0 ${isNavigable
        ? "cursor-pointer hover:bg-slate-50"
        : "cursor-default"
        }`}
      title={isNavigable ? `View ${label}` : label}
    >
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ring-1 ring-black/[0.03] ${colorClasses[metric.color]}`}
      >
        {metric.icon}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-600">
        {label}
      </span>
      <span
        className={`shrink-0 text-right text-base font-semibold tabular-nums ${needsAttention ? "text-rose-600" : "text-slate-800"
          }`}
      >
        {formatMetricValue(property, metric)}
      </span>
    </button>
  );
}

function PropertyCard({
  property,
  defaultExpanded = true,
  onMetricClick,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const inventory = [
    { label: "Rooms", value: property.rooms },
    { label: "Beds", value: property.beds },
    { label: "Occupied", value: property.occupied },
    { label: "Vacant", value: property.vacant },
  ];

  return (
    <article
      data-pdf-card={property.id}
      className="property-card min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <button
        type="button"
        onClick={() => setExpanded((previous) => !previous)}
        aria-expanded={expanded}
        className="flex min-h-[76px] w-full items-center gap-3 rounded-t-xl border-b border-slate-100 bg-slate-50 px-4 py-3 text-left transition-colors hover:bg-indigo-50/60"

      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-xl text-indigo-600 ring-1 ring-indigo-100">
          <FaBuilding size={18} className="text-primary-color" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold text-slate-900">
              {property.name}
            </span>
          </span>

          <span className="mt-1 block truncate text-xs text-slate-500">
            {property.address}
          </span>
        </span>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100">
          {expanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
        </span>
      </button>

      {expanded && (
        <>
          <div className="px-3 pb-2">
            {metricGroups.map((group, index) => (
              <section key={group.title}>
                <div
                  className={`flex items-center justify-between px-1 pb-2 pt-4 ${index > 0 ? "border-t border-slate-100" : ""
                    }`}
                >
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    {group.title}
                  </h3>
                </div>

                {group.metrics.map((metric) => (
                  <PropertyMetric
                    key={metric.key}
                    property={property}
                    metric={metric}
                    onMetricClick={onMetricClick}
                  />
                ))}
              </section>
            ))}
          </div>
        </>
      )}
    </article>
  );
}

function createCombinedProperty(properties) {
  const numericMetricKeys = [
    ...new Set(metricGroups.flatMap((group) =>
      group.metrics.map((metric) => metric.key)
    )),
    "rooms",
    "beds",
    "occupied",
    "vacant",
  ];

  const totals = {};
  numericMetricKeys.forEach((key) => {
    totals[key] = properties.reduce(
      (sum, property) => sum + (Number(property[key]) || 0),
      0
    );
  });

  return {
    ...totals,
    id: "combined",
    name: "Combined Summary",
    location: `${properties.length} properties`,
    isCurrent: false,
  };
}

export default function PropertiesOverview({
  data,
  combinedData,
  onMetricClick,
  onPropertyClick,
}) {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDefaultersModal, setShowDefaultersModal] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const pdfContentRef = useRef(null);

  const properties = useMemo(() => {
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.propertyWiseDashboardData)
        ? data.propertyWiseDashboardData
        : [];

    return list.map((property) => ({
      ...property,
      name: property.name ?? "",
      address: property.address ?? "",
      group: property.group ?? "",
    }));
  }, [data]);

  const [search, setSearch] = useState("");
  const [showCombined, setShowCombined] = useState(false);

  const combinedProperty = useMemo(() => {
    const source = combinedData ?? createCombinedProperty(properties);

    return {
      ...source,
      id: "combined",
      name: source.name ?? "Combined Summary",
      address: source.address ?? `${properties.length} properties`,
    };
  }, [combinedData, properties]);

  const handleMetricClick = (property, metric) => {
    if (metric.key === "rentDefaulters") {
      const count = Number(property?.rentDefaulters || 0);
      if (count <= 0) return;
      const propId = property.id === "combined" ? 0 : property.id;
      setSelectedPropertyId(propId);
      setShowDefaultersModal(true);
      return;
    }
    const nav = getNavigationForMetric(metric.key);

    if (nav) {
      const isCombined = property.id === "combined";

      navigate(nav.route, {
        state: {
          startDate: nav.startDate,
          endDate: nav.endDate,
          filter: nav.filter ?? "",
          propId: isCombined ? undefined : property.id,
          from: "properties-overview",
        },
      });
      return;
    }
    onMetricClick?.(property, metric);
  };

  const handleDownloadPDF = async () => {
    if (isGenerating) return;

    const element = pdfContentRef.current;

    if (!element) {
      alert("Property content not found.");
      return;
    }

    const items = Array.from(element.querySelectorAll(".property-pdf-item"));

    if (!items.length) {
      alert("No property cards found.");
      return;
    }

    setIsGenerating(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 50));
      await document.fonts.ready;

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const margin = 8;
      const gap = 4;
      const headerHeight = 16;
      const footerHeight = 10;

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const columnWidth = (pageWidth - margin * 2 - gap) / 2;

      const contentTop = margin + headerHeight;
      const contentBottom = pageHeight - margin - footerHeight;
      const maxCardHeight = contentBottom - contentTop;

      const generatedAt = new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      let y = contentTop;

      for (let i = 0; i < items.length; i += 2) {
        const rowItems = items.slice(i, i + 2);

        const canvases = await Promise.all(
          rowItems.map(async (item) => {
            const card = item.querySelector(".property-card");

            if (!card) {
              throw new Error("Property card not found.");
            }

            const cardWidth = Math.ceil(card.getBoundingClientRect().width);

            return html2canvas(card, {
              scale: 2,
              useCORS: true,
              backgroundColor: "#ffffff",
              logging: false,
              windowWidth: document.documentElement.clientWidth,
              scrollX: 0,
              scrollY: 0,

              onclone: (clonedDocument) => {
                const clonedCard = clonedDocument.querySelector(
                  `[data-pdf-card="${card.dataset.pdfCard}"]`
                );

                if (!clonedCard) return;

                clonedCard.style.width = `${cardWidth}px`;
                clonedCard.style.minWidth = `${cardWidth}px`;
                clonedCard.style.maxWidth = `${cardWidth}px`;
                clonedCard.style.boxSizing = "border-box";

                const wrapper = clonedCard.parentElement;
                if (wrapper) {
                  wrapper.style.width = `${cardWidth}px`;
                  wrapper.style.minWidth = `${cardWidth}px`;
                }

                const TEXT_LIFT = 5;
                const GLYPH_LIFT = 5;

                clonedCard.style.height = "auto";
                clonedCard.style.maxHeight = "none";
                clonedCard.style.overflow = "visible";

                clonedCard.querySelectorAll("[class*='truncate']").forEach((el) => {
                  el.style.overflow = "visible";
                  el.style.textOverflow = "clip";
                  el.style.whiteSpace = "nowrap";
                });

                clonedCard.querySelectorAll("article > button .truncate").forEach((el) => {
                  el.style.position = "relative";
                  el.style.top = `-${TEXT_LIFT - 2}px`;
                });

                clonedCard.querySelectorAll("section button").forEach((button) => {
                  button.style.height = "auto";
                  button.style.minHeight = "43px";
                  button.style.overflow = "visible";
                  button.style.display = "flex";
                  button.style.alignItems = "center";
                  button.style.lineHeight = "1";
                });

                clonedCard.querySelectorAll("section button > span").forEach((span) => {
                  span.style.display = "flex";
                  span.style.alignItems = "center";
                  span.style.lineHeight = "1";
                  span.style.overflow = "visible";
                });

                clonedCard
                  .querySelectorAll("section button > span:last-child")
                  .forEach((value) => {
                    value.style.flexShrink = "0";
                    value.style.whiteSpace = "nowrap";
                    value.style.marginLeft = "auto";
                    value.style.justifyContent = "flex-end";
                  });

                clonedCard
                  .querySelectorAll("section button > span:first-child")
                  .forEach((tile) => {
                    tile.style.boxSizing = "border-box";
                    tile.style.paddingBottom = `${GLYPH_LIFT * 2}px`;
                  });

                clonedCard
                  .querySelectorAll("section button > span:not(:first-child)")
                  .forEach((el) => {
                    el.style.position = "relative";
                    el.style.top = `-${TEXT_LIFT}px`;
                  });
              },
            });
          })
        );

        const images = canvases.map((canvas) => {
          const imageHeight = (canvas.height * columnWidth) / canvas.width;
          const scale = Math.min(1, maxCardHeight / imageHeight);

          return {
            data: canvas.toDataURL("image/jpeg", 0.95),
            width: columnWidth * scale,
            height: imageHeight * scale,
          };
        });

        const rowHeight = Math.max(...images.map((image) => image.height));

        if (y + rowHeight > contentBottom) {
          pdf.addPage();
          y = contentTop;
        }

        images.forEach((image, index) => {
          pdf.addImage(
            image.data,
            "JPEG",
            margin + index * (columnWidth + gap),
            y,
            image.width,
            image.height
          );
        });

        y += rowHeight + gap;

        if (i + 2 < items.length && y + 40 > contentBottom) {
          pdf.addPage();
          y = contentTop;
        }
      }

      const totalPages = pdf.getNumberOfPages();

      for (let page = 1; page <= totalPages; page++) {
        pdf.setPage(page);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.setTextColor(30, 41, 59);
        pdf.text("Property Overview Report", margin, margin + 6);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        pdf.setTextColor(100, 116, 139);
        pdf.text(generatedAt, pageWidth - margin, margin + 6, { align: "right" });
        pdf.setDrawColor(226, 232, 240);
        pdf.setLineWidth(0.3);
        pdf.line(margin, margin + 10, pageWidth - margin, margin + 10);

        const footerLineY = pageHeight - margin - footerHeight + 3;
        const footerTextY = pageHeight - margin;

        pdf.line(margin, footerLineY, pageWidth - margin, footerLineY);
        pdf.setFontSize(7);
        pdf.setTextColor(100, 116, 139);
        pdf.text("Generated by Kipinn", margin, footerTextY);
        pdf.text(`Page ${page} of ${totalPages}`, pageWidth - margin, footerTextY, {
          align: "right",
        });
      }

      pdf.save("Properties-Overview.pdf");
    } catch (error) {
      console.error("PDF export failed:", error);
      alert(`PDF export failed: ${error.message}`);
    }
    finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-1">
      <div className="flex flex-col gap-4 pt-2 pb-3">
        <div className="flex items-center justify-end gap-3">
          <label
            className={`flex h-10 cursor-pointer select-none items-center gap-2.5 rounded-lg border px-3.5 text-sm font-medium transition-colors ${showCombined
              ? "border-primary-color bg-white text-slate-600"
              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
          >
            <input
              type="checkbox"
              checked={showCombined}
              onChange={(e) => setShowCombined(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 accent-primary-color"
            />
            All Properties
          </label>

          <button
            type="button"
            onClick={handleDownloadPDF}
            disabled={isGenerating}
            title="Download PDF report"
            aria-label="Download PDF report"
            aria-busy={isGenerating}
            className="inline-flex h-10 min-w-[140px] items-center justify-center gap-2 rounded-lg bg-primary-color px-4 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-color/70 hover:shadow focus:outline-none focus:ring-2 focus:ring-primary-color focus:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-primary-color/10 disabled:shadow-none disabled:active:scale-100"
          >
            {isGenerating ? (
              <>
                <FaSpinner size={14} className="animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <FaFileDownload size={14} />
                <span>Download PDF</span>
              </>
            )}
          </button>
        </div>
      </div>

      {showCombined && (
        <div ref={pdfContentRef} className="mb-5" data-pdf-content="true">
          <div className="property-pdf-item min-w-0">
            <PropertyCard
              property={combinedProperty}
              defaultExpanded
              // onMetricClick={onMetricClick}
              onMetricClick={handleMetricClick}
            />
          </div>
        </div>
      )}

      {!showCombined && (
        <>
          {properties.length > 0 ? (
            <div
              ref={pdfContentRef}
              data-pdf-content="true"
              className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2 2xl:grid-cols-3"
            >
              {properties.map((property) => (
                <div
                  key={property.id}
                  className="property-pdf-item min-w-0"
                  onDoubleClick={() => onPropertyClick?.(property)}
                >
                  <PropertyCard
                    property={property}
                    defaultExpanded
                    onMetricClick={handleMetricClick}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-14 text-center shadow-sm">
              <p className="text-sm font-semibold text-slate-800">
                No properties found
              </p>
            </div>
          )}
        </>
      )}

      <SideModal isOpen={showDefaultersModal} onClose={() => setShowDefaultersModal(false)} level="base">
        <RentDefaultersModal
          isOpen={showDefaultersModal}
          onClose={() => {
            setShowDefaultersModal(false);
            setSelectedPropertyId(null);
          }}
          propId={selectedPropertyId}
        />
      </SideModal>
    </div>
  );
}
